import {
  AdImpressionEvent,
  AdNotRenderedEvent,
  AdRenderedEvent,
  AnalyticsBackend,
  AnalyticsEvent,
  AnalyticsService,
  ContentClickedEvent,
  ContentDismissedEvent,
  ContentImpressionEvent,
  ErrorEvent,
  FirstViewEvent,
  InterestClickedEvent,
  LinkClickedEvent,
  PageLoadedEvent,
  PageNotLoadedEvent,
  PageViewEvent,
  PrebidAuctionEndEvent,
  PrebidBidWonEvent,
  PrebidRenderFailedEvent,
  PrebidRenderSucceededEvent,
  ScrollEvent,
  SlotDuplicateRefreshEvent,
  SlotLoadedEvent,
  SlotRenderedEvent,
  SlotResponseReceivedEvent,
  StoryDismissedEvent,
  SwipeEvent,
  TapEvent,
  WidgetClickEvent,
  WidgetImpressionEvent,
} from './AnalyticsService';
import { LOOKBOOK_PARAMETERS_TO_COPY } from '@pages/Root/pages/Lookbook/contants.ts';
import { eventTypes } from '@utils/constants.ts';

export class AnalyticsCompositor implements AnalyticsService {
  private providers: AnalyticsBackend[];

  constructor(...providers: AnalyticsBackend[]) {
    this.providers = providers;
  }

  public init = async (): Promise<void> => {
    await Promise.all(this.providers.map((p) => p.init()));
  };

  public send = (event: AnalyticsEvent): void => {
    this.providers.forEach((p) => p.send(event));
  };

  public trackEvent = (event: AnalyticsEvent): void => {
    this.send(event);
  };

  public getRequiredLookEventParams = (searchParams: URLSearchParams): Record<string, string> => {
    const eventData: Record<string, string> = {};
    for (const parameter of LOOKBOOK_PARAMETERS_TO_COPY) {
      const parameterValue = searchParams?.get(parameter);
      if (parameterValue) {
        eventData[parameter] = parameterValue;
      }
    }
    return eventData;
  };

  public emitContentClickedEvent = (event: ContentClickedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.CONTENT_CLICK, params: { ...reqParams, ...event } });
  };

  public emitInterestClickedEvent = (event: InterestClickedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.INTEREST_CLICK, params: { ...event, ...reqParams } });
  };

  public emitLinkClickedEvent = (event: LinkClickedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.LINK_CLICK, params: { ...event, ...reqParams } });
  };

  public emitTapEvent = (event: TapEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.TAP, params: { ...event, ...reqParams } });
  };

  public emitSwipeEvent = (event: SwipeEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.SWIPE, params: { ...event, ...reqParams } });
  };

  public emitImpressionEvent = (event: AdImpressionEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.IMPRESSION, params: { ...event, ...reqParams } });
  };

  public emitViewableImpressionEvent = (event: AdImpressionEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.VIEWABLE_IMPRESSION, params: { ...event, ...reqParams } });
  };

  public emitAdImpressionEvent = (event: AdImpressionEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.AD_IMPRESSION, params: { ...event, ...reqParams } });
  };

  public emitAdRenderedEvent = (event: AdRenderedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.AD_RENDERED, params: { ...event, ...reqParams } });
  };

  public emitAdNotRenderedEvent = (event: AdNotRenderedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.AD_NOT_RENDERED, params: { ...event, ...reqParams } });
  };

  public emitPageViewEvent = (event: PageViewEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.PAGE_VIEW, params: { ...event, ...reqParams } });
  };

  public emitScrollEvent = (event: ScrollEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.SCROLL, params: { ...event, ...reqParams } });
  };

  public emitFirstViewEvent = (event: FirstViewEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.FIRST_VISIT, params: { ...event, ...reqParams } });
  };

  public emitContentDismissedEvent = (event: ContentDismissedEvent): void => {
    const { searchParams, duration, reason } = event;
    const reqParams = this.getRequiredLookEventParams(searchParams);
    this.send({ name: eventTypes.CONTENT_DISMISSED, params: { duration, reason, ...reqParams } });
  };

  public emitStoryDismissedEvent = (event: StoryDismissedEvent): void => {
    const { searchParams, duration, reason, contentId } = event;
    const reqParams = this.getRequiredLookEventParams(searchParams);
    this.send({ name: eventTypes.STORY_DISMISSED, params: { duration, reason, contentId, ...reqParams } });
  };

  public emitContentImpressionEvent = (event: ContentImpressionEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.CONTENT_IMPRESSION, params: { ...event, ...reqParams } });
  };

  public emitPageLoadedEvent = (event: PageLoadedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.PAGE_LOADED, params: { ...event, loadTime: event.loadTime, ...reqParams } });
  };

  public emitPageNotLoadedEvent = (event: PageNotLoadedEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({
      name: eventTypes.PAGE_NOT_LOADED,
      params: { ...event, errorType: event.errorType, errorMessage: event.errorMessage, ...reqParams },
    });
  };

  public emitWidgetImpressionEvent = (event: WidgetImpressionEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({ name: eventTypes.WIDGET_IMPRESSION, params: { ...event, source: event.source, ...reqParams } });
  };

  public emitWidgetClickedEvent = (event: WidgetClickEvent): void => {
    const reqParams = this.getRequiredLookEventParams(event.searchParams);
    this.send({
      name: eventTypes.WIDGET_CLICK,
      params: { ...event, source: event.source, click_target: event.target, ...reqParams },
    });
  };

  public emitErrorEvent = (event: ErrorEvent): void => {
    this.send({ name: eventTypes.ERROR, params: { ...event } });
  };

  public emitSlotRenderedEvent = (event: SlotRenderedEvent): void => {
    this.send({ name: eventTypes.AD_SLOT_RENDERED, params: { ...event } });
  };

  public emitPrebidAuctionEndEvent = (event: PrebidAuctionEndEvent): void => {
    this.send({ name: eventTypes.AD_PREBID_AUCTION_END, params: { ...event } });
  };

  public emitPrebidBidWonEvent = (event: PrebidBidWonEvent): void => {
    this.send({ name: eventTypes.AD_PREBID_BID_WON, params: { ...event } });
  };

  public emitPrebidRenderSucceededEvent = (event: PrebidRenderSucceededEvent): void => {
    this.send({ name: eventTypes.AD_PREBID_RENDER_SUCCEEDED, params: { ...event } });
  };

  public emitPrebidRenderFailedEvent = (event: PrebidRenderFailedEvent): void => {
    this.send({ name: eventTypes.AD_PREBID_RENDER_FAILED, params: { ...event } });
  };

  public emitSlotLoadedEvent = (event: SlotLoadedEvent): void => {
    this.send({ name: eventTypes.AD_SLOT_LOADED, params: { ...event } });
  };

  public emitSlotResponseReceivedEvent = (event: SlotResponseReceivedEvent): void => {
    this.send({ name: eventTypes.AD_SLOT_RESPONSE_RECEIVED, params: { ...event } });
  };

  public emitSlotDuplicateRefreshEvent = (event: SlotDuplicateRefreshEvent): void => {
    this.send({ name: eventTypes.AD_SLOT_DUPLICATE_REFRESH, params: { ...event } });
  };
}
